import {
  Box,
  Modal,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import productService from "../../../../services/productService";
import { useEffect, useState } from "react";

interface ExcelLog {
  errorMessage: string;
  createDate: string;
  productBarcode: string;
}

const ExcelRunTime = ({ open, handleCloseModal, excelId }) => {
  const [logs, setLogs] = useState<ExcelLog[]>([]);
  const [loading, setLoading] = useState(false);
  const getExcelLogs = async (excelId: number) => {
    try {
      setLoading(true);
      const response = await productService.getExcelRunTime(excelId);
      console.log("excelll", response);
      const logsData = response?.data?.data?.data;
      setLogs(Array.isArray(logsData) ? logsData : []);
    } catch (error) {
      console.error("Excel log fetch error:", error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && excelId) {
      getExcelLogs(excelId);
    }
  }, [open, excelId]);

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="excel-runtime-modal"
      aria-describedby="excel-runtime-logs"
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: "90%", md: "80%" },
          maxWidth: 800,
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: { xs: 1, sm: 2 },
          p: { xs: 2, sm: 3, md: 4 },
          maxHeight: { xs: "80vh", sm: "85vh", md: "90vh" },
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
            "&:hover": {
              background: "#555",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: { xs: 1.5, sm: 2 },
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            id="excel-runtime-modal"
            variant="h6"
            component="h2"
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
              fontWeight: "medium",
            }}
          >
            Excel İşlem Durumu
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              color: (theme) => theme.palette.grey[500],
              padding: { xs: 0.5, sm: 1 },
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
          </IconButton>
        </Box>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: { xs: 2, sm: 3 },
              minHeight: "200px",
              alignItems: "center",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: { xs: 1, sm: 2 },
              "& .MuiTable-root": {
                minWidth: { xs: 650, sm: 750 },
              },
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                      py: { xs: 1, sm: 1.5 },
                    }}
                  >
                    Mesaj
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                      py: { xs: 1, sm: 1.5 },
                    }}
                  >
                    Tarih
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                      py: { xs: 1, sm: 1.5 },
                    }}
                  >
                    Ürün Barkodu
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs?.map((log, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        py: { xs: 1, sm: 1.5 },
                        maxWidth: { xs: "150px", sm: "200px", md: "300px" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {log.errorMessage}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        py: { xs: 1, sm: 1.5 },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {new Date(log.createDate).toLocaleString("tr-TR", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        py: { xs: 1, sm: 1.5 },
                      }}
                    >
                      {log.productBarcode}
                    </TableCell>
                  </TableRow>
                ))}
                {logs?.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        py: { xs: 2, sm: 3 },
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        color: "text.secondary",
                      }}
                    >
                      Kayıt bulunamadı
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Modal>
  );
};

export default ExcelRunTime;
